/**
 * Naming the environment a row belongs to.
 *
 * Extracted from `ActiveThreadsSection` when the file cap gate bit. Assembling
 * the label needs four registries, and gathering them was most of what that
 * component said about environments without being what the component is for.
 *
 * The local environment answers for itself: it has a descriptor with a chosen
 * label, and falling through to the runtime resolver for it would print a
 * device name where the user expects "This Device".
 */
import { useCallback, useMemo } from "react";

import type { EnvironmentId } from "@cmmd-forge/contracts";
import { buildRegisteredDesktopRuntimeMap } from "./registeredDesktopRuntimeMap";
import {
  resolveRuntimeEnvironmentLabel,
  type RuntimeEnvironmentLabelContext,
} from "./buildRuntimeProjectGroups";

type DesktopRuntimeMapInput = Parameters<typeof buildRegisteredDesktopRuntimeMap>[0];
type InventoryRuntimeEntry =
  RuntimeEnvironmentLabelContext["inventoryRuntimeById"] extends ReadonlyMap<string, infer TEntry>
    ? TEntry
    : never;

export function useResolveEnvironmentLabel(input: {
  readonly savedEnvironmentRegistry: RuntimeEnvironmentLabelContext["savedEnvironmentRegistry"];
  readonly savedEnvironmentRuntimeById: RuntimeEnvironmentLabelContext["savedEnvironmentRuntimeById"];
  readonly deviceRegistry: {
    readonly runtimes?: DesktopRuntimeMapInput["registeredRuntimes"];
    readonly devices?: DesktopRuntimeMapInput["registeredDevices"];
    readonly hosts?: DesktopRuntimeMapInput["registeredHosts"];
  } | null;
  readonly currentRegisteredRuntimeId: DesktopRuntimeMapInput["currentRegisteredRuntimeId"];
  readonly currentRegisteredDeviceKey: DesktopRuntimeMapInput["currentRegisteredDeviceKey"];
  readonly runtimeInventoryRuntimes: readonly InventoryRuntimeEntry[];
  readonly primaryEnvironmentId: EnvironmentId | null;
  readonly primaryEnvironmentLabel: string | null;
}): (environmentId: EnvironmentId) => string {
  const {
    savedEnvironmentRegistry,
    savedEnvironmentRuntimeById,
    deviceRegistry,
    currentRegisteredRuntimeId,
    currentRegisteredDeviceKey,
    runtimeInventoryRuntimes,
    primaryEnvironmentId,
    primaryEnvironmentLabel,
  } = input;

  const context = useMemo<RuntimeEnvironmentLabelContext>(
    () => ({
      savedEnvironmentRegistry,
      savedEnvironmentRuntimeById,
      registeredDesktopRuntimeById: buildRegisteredDesktopRuntimeMap({
        registeredRuntimes: deviceRegistry?.runtimes ?? [],
        registeredDevices: deviceRegistry?.devices ?? [],
        registeredHosts: deviceRegistry?.hosts ?? [],
        currentRegisteredRuntimeId,
        currentRegisteredDeviceKey,
      }),
      inventoryRuntimeById: new Map(
        runtimeInventoryRuntimes.map((entry) => [entry.runtime.id, entry]),
      ),
    }),
    [
      savedEnvironmentRegistry,
      savedEnvironmentRuntimeById,
      deviceRegistry?.runtimes,
      deviceRegistry?.devices,
      deviceRegistry?.hosts,
      currentRegisteredRuntimeId,
      currentRegisteredDeviceKey,
      runtimeInventoryRuntimes,
    ],
  );

  return useCallback(
    (environmentId: EnvironmentId): string =>
      environmentId === primaryEnvironmentId
        ? (primaryEnvironmentLabel ?? "This Device")
        : resolveRuntimeEnvironmentLabel(environmentId, context),
    [primaryEnvironmentId, primaryEnvironmentLabel, context],
  );
}
